import type { AdjustBudget, PoolLedger } from '@myrewards/contracts';
import type { HistoryRow } from './model';

// Budget pools edited in place (X4, K31): what an edit writes to the ledger, and a pool's
// ledger as a value over time for its History drawer.

export interface BudgetValue {
  cashCents: number;
  options: number;
}

export type LedgerWrite = Pick<AdjustBudget, 'kind' | 'cashCents' | 'options'>;

/**
 * The ledger entries an edit makes. Setting a pool's first budget is one Initial budget entry.
 * Changing it is an Addition or a Reduction; if cash goes up while options go down (or the other
 * way), that's one of each, so every entry has one direction.
 */
export function budgetWrites(before: BudgetValue | null, after: BudgetValue): LedgerWrite[] {
  if (
    !Number.isSafeInteger(after.cashCents) ||
    !Number.isSafeInteger(after.options) ||
    after.cashCents < 0 ||
    after.options < 0
  )
    return [];
  if (before === null)
    return after.cashCents > 0 || after.options > 0
      ? [{ kind: 'initial', cashCents: after.cashCents, options: after.options }]
      : [];
  const cash = after.cashCents - before.cashCents;
  const options = after.options - before.options;
  const up = { cashCents: Math.max(cash, 0), options: Math.max(options, 0) };
  const down = { cashCents: Math.max(-cash, 0), options: Math.max(-options, 0) };
  const writes: LedgerWrite[] = [];
  if (up.cashCents > 0 || up.options > 0) writes.push({ kind: 'addition', ...up });
  if (down.cashCents > 0 || down.options > 0) writes.push({ kind: 'reduction', ...down });
  return writes;
}

/**
 * An edit's entries as one request (APP-148 R73): the first entry, and a second one the other way
 * as `also`, so the server writes both or neither, with one record and one notice when it's done
 * for an Executive. Null when the edit writes nothing.
 */
export function adjustBodyOf(
  writes: readonly LedgerWrite[],
): Pick<AdjustBudget, 'kind' | 'cashCents' | 'options' | 'also'> | null {
  const [first, second] = writes;
  if (first === undefined) return null;
  if (second === undefined || second.kind === 'initial') return { ...first };
  return {
    ...first,
    also: { kind: second.kind, cashCents: second.cashCents, options: second.options },
  };
}

/**
 * A pool's ledger as settings-history rows of its cash budget, so the History drawer can draw the
 * value over time and answer "as of". Voided entries don't change the total.
 */
export function ledgerHistoryRows(ledger: PoolLedger): HistoryRow[] {
  let cash = 0;
  const rows: HistoryRow[] = [];
  for (const e of [...ledger.entries].sort((a, b) => a.at.localeCompare(b.at))) {
    if (e.voided) continue;
    cash += e.cashCents;
    rows.push({
      id: e.id,
      value: cash,
      effectiveFrom: null,
      recordedAt: e.at,
      by: e.by,
      previewedBy: e.previewedBy,
      reason: e.reason,
    });
  }
  return rows.reverse();
}

/** Where Budget pools' cards are, for links and for opening the page at one. */
export const POOLS_ANCHOR = 'budget-pools';
export const LEDGER_ANCHOR = 'budget-ledger';
